/**
 * Warum das Mikrofon nicht nutzbar ist - oder `ok`.
 *
 * Die Namen sind fachlich, nicht technisch; die Oberfläche übersetzt sie.
 */
export type MicrophoneResult =
  | 'ok'
  /** Der Browser hat die Freigabe verweigert. */
  | 'microphone-denied'
  /** Es ist überhaupt kein Mikrofon angeschlossen. */
  | 'microphone-missing'
  /** Das Gerät ist da, aber von etwas anderem belegt. */
  | 'microphone-busy'
  /** Kein `getUserMedia` oder kein sicherer Kontext. */
  | 'microphone-unavailable'
  /** Alles andere. */
  | 'microphone-error';

/**
 * Prüft das Mikrofon - mit einem echten Medienstrom, nicht über die
 * Permissions API.
 *
 * `navigator.permissions.query({ name: 'microphone' })` beantwortet nur, was
 * der Nutzer irgendwann erlaubt hat. Ob jetzt tatsächlich ein Strom zu
 * bekommen ist, sagt ausschließlich `getUserMedia`. Deshalb wird hier
 * aufgerufen, nicht abgefragt.
 *
 * **Der Strom wird sofort wieder freigegeben.** Das ist nicht Kosmetik:
 *
 * - Ein offener Strom hält das Mikrofon aktiv. Der Browser zeigt die rote
 *   Aufnahmeanzeige, obwohl gar nicht telefoniert wird.
 * - Vor allem aber: ein zweiter Aufruf von `getUserMedia` kann scheitern,
 *   solange der erste Strom noch lebt - Chrome meldet dann
 *   `NotReadableError`, weil das Gerät belegt ist. Genau das hat dazu
 *   geführt, dass die Oberfläche hartnäckig "Mikrofon freigeben" anzeigte,
 *   obwohl die Freigabe längst erteilt war.
 *
 * Den Strom für das Gespräch holt sich SIP.js später selbst.
 */
export async function pruefeMikrofon(): Promise<MicrophoneResult> {
  // Ohne sicheren Kontext gibt es `getUserMedia` in Chrome gar nicht. Das ist
  // kein Nutzerfehler und soll auch nicht als solcher gemeldet werden.
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return 'microphone-unavailable';
  }
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
    return 'microphone-unavailable';
  }

  let strom: MediaStream;
  try {
    strom = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (fehler) {
    return nachFehlername(fehler);
  }

  // Sofort freigeben - siehe oben.
  //
  // Scheitert das Freigeben, ist die Prüfung selbst trotzdem gelungen: der
  // Browser hat einen Strom geliefert. Dann darf das Telefon nicht wegen des
  // Aufräumens als defekt gelten.
  try {
    for (const spur of strom.getTracks()) {
      spur.stop();
    }
  } catch {
    // Folgenlos - der Strom wird ohnehin nicht weiterverwendet.
  }
  return 'ok';
}

/**
 * Ordnet die Fehlernamen des Browsers zu.
 *
 * Vorher wurde jeder Fehler unterschiedslos als "Mikrofon nicht freigegeben"
 * gemeldet - auch ein fehlendes Gerät oder ein belegtes Mikrofon. Das hat die
 * Fehlersuche aktiv behindert.
 */
function nachFehlername(fehler: unknown): MicrophoneResult {
  const name = fehler instanceof Error ? fehler.name : '';

  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'microphone-denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'microphone-missing';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'microphone-busy';
    case 'TypeError':
      // `navigator.mediaDevices` war nicht da.
      return 'microphone-unavailable';
    default:
      return 'microphone-error';
  }
}
